/**
 * エラーハンドリング統一システム
 * アプリケーション全体のエラー処理を標準化
 */

import { logger } from './logger';

export enum ErrorType {
  API_ERROR = 'API_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  AUTH_ERROR = 'AUTH_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: any;
  context?: any;
  userMessage: string;
  retryable: boolean;
}

/**
 * カスタムエラークラス
 */
export class YokaiAppError extends Error {
  public readonly type: ErrorType;
  public readonly userMessage: string;
  public readonly retryable: boolean;
  public readonly context?: any;
  public readonly originalError?: any;

  constructor(appError: AppError) {
    super(appError.message);
    this.name = 'YokaiAppError';
    this.type = appError.type;
    this.userMessage = appError.userMessage;
    this.retryable = appError.retryable;
    this.context = appError.context;
    this.originalError = appError.originalError;
  }
}

/**
 * エラーハンドラークラス
 */
export class ErrorHandler {
  /**
   * APIエラーを処理
   */
  static handleApiError(error: any, service: string, endpoint?: string): YokaiAppError {
    logger.apiError(service, endpoint || 'unknown', error);

    // HTTP ステータスコードに基づく分類
    if (error?.response?.status) {
      const status = error.response.status;
      
      if (status === 401) {
        return new YokaiAppError({
          type: ErrorType.AUTH_ERROR,
          message: `${service} API認証エラー: ${error.message}`,
          userMessage: 'APIキーが無効です。設定を確認してください。',
          retryable: false,
          originalError: error,
          context: { service, endpoint, status }
        });
      }
      
      if (status === 403) {
        return new YokaiAppError({
          type: ErrorType.AUTH_ERROR,
          message: `${service} API権限エラー: ${error.message}`,
          userMessage: 'API利用権限がありません。アカウント設定を確認してください。',
          retryable: false,
          originalError: error,
          context: { service, endpoint, status }
        });
      }
      
      if (status === 429) {
        return new YokaiAppError({
          type: ErrorType.RATE_LIMIT_ERROR,
          message: `${service} API利用制限: ${error.message}`,
          userMessage: 'API利用制限に達しました。しばらく待ってから再試行してください。',
          retryable: true,
          originalError: error,
          context: { service, endpoint, status }
        });
      }
      
      if (status >= 500) {
        return new YokaiAppError({
          type: ErrorType.API_ERROR,
          message: `${service} サーバーエラー: ${error.message}`,
          userMessage: 'サーバーに問題が発生しています。しばらく待ってから再試行してください。',
          retryable: true,
          originalError: error,
          context: { service, endpoint, status }
        });
      }
    }

    // タイムアウトエラー
    if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
      return new YokaiAppError({
        type: ErrorType.TIMEOUT_ERROR,
        message: `${service} タイムアウト: ${error.message}`,
        userMessage: 'リクエストがタイムアウトしました。ネットワーク接続を確認してください。',
        retryable: true,
        originalError: error,
        context: { service, endpoint }
      });
    }

    // ネットワークエラー
    if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED' || !navigator.onLine) {
      return new YokaiAppError({
        type: ErrorType.NETWORK_ERROR,
        message: `${service} ネットワークエラー: ${error.message}`,
        userMessage: 'ネットワーク接続を確認してください。',
        retryable: true,
        originalError: error,
        context: { service, endpoint }
      });
    }

    // その他のAPIエラー
    return new YokaiAppError({
      type: ErrorType.API_ERROR,
      message: `${service} APIエラー: ${error.message}`,
      userMessage: `${service}の処理に失敗しました。`,
      retryable: false,
      originalError: error,
      context: { service, endpoint }
    });
  }

  /**
   * バリデーションエラーを処理
   */
  static handleValidationError(message: string, field?: string): YokaiAppError {
    logger.warn('VALIDATION', `バリデーションエラー: ${message}`, { field });

    return new YokaiAppError({
      type: ErrorType.VALIDATION_ERROR,
      message: `バリデーションエラー: ${message}`,
      userMessage: message,
      retryable: false,
      context: { field }
    });
  }

  /**
   * 設定エラーを処理
   */
  static handleConfigError(message: string, key?: string): YokaiAppError {
    logger.error('CONFIG', `設定エラー: ${message}`, { key });

    return new YokaiAppError({
      type: ErrorType.VALIDATION_ERROR,
      message: `設定エラー: ${message}`,
      userMessage: message,
      retryable: false,
      context: { key }
    });
  }

  /**
   * 汎用エラーを処理
   */
  static handleGenericError(error: any, context?: string): YokaiAppError {
    logger.error('GENERIC', `汎用エラー: ${error.message || error}`, { context, error });

    if (error instanceof YokaiAppError) {
      return error;
    }

    return new YokaiAppError({
      type: ErrorType.UNKNOWN_ERROR,
      message: error.message || String(error),
      userMessage: '予期しないエラーが発生しました。',
      retryable: false,
      originalError: error,
      context: { context }
    });
  }

  /**
   * エラーから再試行可能かを判定
   */
  static isRetryable(error: YokaiAppError | Error): boolean {
    if (error instanceof YokaiAppError) {
      return error.retryable;
    }
    
    // 一般的なError の場合はタイムアウトやネットワークエラーなら再試行可能と判定
    const message = error.message?.toLowerCase() || '';
    return message.includes('timeout') || 
           message.includes('network') || 
           message.includes('fetch');
  }

  /**
   * ユーザー向けメッセージを取得
   */
  static getUserMessage(error: YokaiAppError | Error): string {
    if (error instanceof YokaiAppError) {
      return error.userMessage;
    }
    
    return '予期しないエラーが発生しました。';
  }
}

/**
 * 便利な関数群
 */

// APIエラーのラッパー関数
export const handleFalApiError = (error: any) => 
  ErrorHandler.handleApiError(error, 'FAL-AI', 'image-generation');

export const handleOpenAiError = (error: any) => 
  ErrorHandler.handleApiError(error, 'OpenAI', 'chat-completion');


// 共通のエラーハンドリング関数
export const safeAsync = async <T>(
  fn: () => Promise<T>,
  context?: string
): Promise<[T | null, YokaiAppError | null]> => {
  try {
    const result = await fn();
    return [result, null];
  } catch (error) {
    const appError = ErrorHandler.handleGenericError(error, context);
    return [null, appError];
  }
};